import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const TodaysEventsWidget = () => {
  const [events, setEvents] = useState([]);
  const [computed, setComputed] = useState({ deadlines: [], litigation_events: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchTodaysEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create fresh date objects to avoid mutation issues
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      const response = await api.get(
        `/calendar/events?start_date=${startOfDay.toISOString()}&end_date=${endOfDay.toISOString()}`
      );
      // B4: same second source as the calendar's day view — the two must agree.
      try {
        const d = now.toISOString().slice(0, 10);
        const ci = await api.get(`/calendar/computed-items?start_date=${d}&end_date=${d}`);
        setComputed(ci.data || { deadlines: [], litigation_events: [] });
      } catch (e) { setComputed({ deadlines: [], litigation_events: [] }); }

      // Ensure data is an array before sorting
      const eventsArray = Array.isArray(response.data) ? response.data : [];
      setEvents(eventsArray.sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodaysEvents();
  }, []);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  };

  if (loading) {
    return (
      <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">Today's Events · {events.length + computed.deadlines.length + computed.litigation_events.length}</h2>
          </div>
        </div>
        <div className="text-center py-6">
          <p className="text-slate-400 mb-3">{error}</p>
          <button
            onClick={fetchTodaysEvents}
            className="text-sm text-blue-300 hover:text-white transition-colors px-4 py-2 bg-blue-500/20 rounded-lg hover:bg-blue-500/30"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-white">Today's Events · {events.length + computed.deadlines.length + computed.litigation_events.length}</h2>
        </div>
        <button
          onClick={() => navigate('/calendar')}
          className="text-sm text-slate-400 hover:text-white flex items-center transition-colors px-3 py-1.5 hover:bg-white/5 rounded-lg"
        >
          View All
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-blue-500/20">
            <Calendar className="w-7 h-7 text-blue-400" />
          </div>
          <p className="text-slate-400 text-sm mb-3">No events today</p>
          <button
            onClick={() => navigate('/calendar')}
            className="text-sm text-blue-300 hover:text-white transition-colors px-4 py-2 bg-blue-500/20 rounded-lg hover:bg-blue-500/30"
          >
            Schedule event
          </button>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {computed.deadlines.map((d) => (
            <div key={`cd-${d.id}`} className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm">
              ⚑ {d.deadline_type || 'Deadline'} · {d.case_name || d.case_id}
            </div>
          ))}
          {computed.litigation_events.map((l) => (
            <div key={`le-${l.id}`} className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm">
              ⚖️ {l.event_type || 'Litigation event'} · {l.case_name || l.case_id}
            </div>
          ))}
          {events.map((event) => (
            <div
              key={event.id}
              onClick={() => navigate('/calendar')}
              className="flex items-start p-3 rounded-xl hover:bg-white/5 cursor-pointer transition-all border border-transparent hover:border-slate-700 group"
            >
              <div
                className="w-1 h-full rounded-full mr-3 flex-shrink-0"
                style={{ backgroundColor: event.color || '#3b82f6', minHeight: '2.5rem' }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white text-sm truncate group-hover:text-blue-200">{event.title}</p>
                <div className="flex items-center text-xs text-slate-400 mt-1">
                  <Clock className="w-3.5 h-3.5 mr-1 text-blue-400" />
                  <span>{formatTime(event.start_time)} - {formatTime(event.end_time)}</span>
                </div>
                {event.location && (
                  <div className="flex items-center text-xs text-slate-500 mt-1">
                    <MapPin className="w-3.5 h-3.5 mr-1 text-cyan-400" />
                    <span className="truncate">{event.location}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {events.length > 4 && (
            <button
              onClick={() => navigate('/calendar')}
              className="w-full text-center text-xs text-slate-400 hover:text-white py-2 transition-colors rounded-lg hover:bg-white/5"
            >
              +{events.length - 4} more
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TodaysEventsWidget;
